import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  AccessControl,
  Button,
  CircularProgressLoader,
  FieldSelect,
  FieldTextInput,
  hasAccess,
  PERMISSION_KEYS,
  PERMISSION_TYPES,
  ToastBar,
  ValidationError,
} from '../../components';
import { EnterViceService } from '../../services';
import type { Preset, PresetPayload, ProviderDescriptor, TestRun } from '../../types/entervice';
import { getDateAndTime } from '../../utils/dateMethods';
import StatusChip from './parts/StatusChip';
import TestLab, { InlineSpec, InlineVideoSpec } from './parts/TestLab';
import { describeError, UiError } from './parts/errors';
import ErrorBox from './parts/ErrorBox';
import '../LayoutWrapperMain/LayoutWrapperMain.module.scss';
import './entervice.scss';

interface FormValues {
  name: string;
  key: string;
  description: string;
  imageModel: string;
  prompt: string;
  negativePrompt: string;
  aspectRatio: string;
  resolution: string;
  videoEnabled: boolean;
  videoModel: string;
  motionPrompt: string;
  durationSec: number | string;
  videoResolution: string;
}

const KEY_REGEX = /^[a-z0-9_]{3,40}$/;
const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p'];

const DEFAULTS: FormValues = {
  name: '',
  key: '',
  description: '',
  imageModel: '',
  prompt: '',
  negativePrompt: '',
  aspectRatio: '9:16',
  resolution: '1k',
  videoEnabled: false,
  videoModel: '',
  motionPrompt: '',
  durationSec: 10,
  videoResolution: '720p',
};

// Mirrors vice-service's Joi rules so the panel rejects bad input before the round trip.
const schema = yup.object().shape({
  name: yup.string().trim().required('Name is required.').min(2, 'Name needs at least 2 characters.').max(60, 'Name can be at most 60 characters.'),
  key: yup.string().trim().required('Key is required.').matches(KEY_REGEX, 'Key: 3-40 characters, lowercase letters, digits and underscore only.'),
  description: yup.string().max(300, 'Description can be at most 300 characters.'),
  imageModel: yup.string().required('Choose an image model.'),
  prompt: yup.string().trim().required('Prompt is required.').min(3, 'Prompt needs at least 3 characters.').max(4000, 'Prompt can be at most 4000 characters.'),
  negativePrompt: yup.string().trim().required('Negative prompt is required.').max(2000, 'Negative prompt can be at most 2000 characters.'),
  aspectRatio: yup.string().required('Choose an aspect ratio.'),
  resolution: yup.string().required('Choose a resolution.'),
  videoEnabled: yup.boolean(),
  videoModel: yup.string().when('videoEnabled', { is: true, then: yup.string().required('Choose a video model.') }),
  motionPrompt: yup.string().max(2000, 'Motion prompt can be at most 2000 characters.'),
  durationSec: yup.mixed().when('videoEnabled', {
    is: true,
    then: yup.number().typeError('Duration must be a number of seconds.').integer('Duration must be whole seconds.').min(1, 'Duration: 1-15 seconds.').max(15, 'Duration: 1-15 seconds.').required('Duration is required.'),
  }),
  videoResolution: yup.string(),
});

const modelKey = (p: ProviderDescriptor) => `${p.provider}::${p.model}`;
const splitModelKey = (value: string) => {
  const [provider, ...rest] = value.split('::');
  return { provider, model: rest.join('::') };
};
const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

const toFormValues = (p: Preset): FormValues => ({
  name: p.name,
  key: p.key,
  description: p.description || '',
  imageModel: `${p.image.provider}::${p.image.model}`,
  prompt: p.image.prompt,
  negativePrompt: p.image.negativePrompt,
  aspectRatio: (p.image.params && p.image.params.aspectRatio) || '9:16',
  resolution: (p.image.params && p.image.params.resolution) || '1k',
  videoEnabled: Boolean(p.video),
  videoModel: p.video ? `${p.video.provider}::${p.video.model}` : '',
  motionPrompt: p.video ? p.video.motionPrompt || '' : '',
  durationSec: p.video ? p.video.durationSec || 10 : 10,
  videoResolution: p.video ? p.video.resolution || '720p' : '720p',
});

const toPayload = (v: FormValues): PresetPayload => {
  const image = splitModelKey(v.imageModel);
  const payload: PresetPayload = {
    name: v.name.trim(),
    key: v.key.trim(),
    description: (v.description || '').trim(),
    image: {
      provider: image.provider,
      model: image.model,
      prompt: v.prompt.trim(),
      negativePrompt: v.negativePrompt.trim(),
      params: { aspectRatio: v.aspectRatio, resolution: v.resolution },
    },
    video: null,
  };
  if (v.videoEnabled && v.videoModel) {
    const video = splitModelKey(v.videoModel);
    payload.video = {
      provider: video.provider,
      model: video.model,
      motionPrompt: (v.motionPrompt || '').trim(),
      durationSec: Number(v.durationSec) || 10,
      resolution: v.videoResolution || '720p',
    };
  }
  return payload;
};

const toOptions = (values: string[]) => values.map((value) => ({ key: value, label: value }));

/** Preset editor + Test Lab for `/entervice/addPreset` and `/entervice/editPreset/:id` (SCRUM-219). */
const ManageEnterVicePreset: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id) && location.pathname.includes('editPreset');
  const canEdit = hasAccess(PERMISSION_KEYS.ENTER_VICE, PERMISSION_TYPES.EDIT);

  const [providers, setProviders] = useState<ProviderDescriptor[]>([]);
  const [preset, setPreset] = useState<Preset | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<UiError | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<UiError | null>(null);
  const [toast, setToast] = useState('');
  const keyTouched = useRef(false);

  const formProps = useForm<FormValues>({ resolver: yupResolver(schema) as any, defaultValues: DEFAULTS });
  const {
    handleSubmit, reset, setValue, getValues, watch, register, formState: { errors },
  } = formProps;
  const nameValue = watch('name');
  const imageModel = watch('imageModel');
  const videoEnabled = watch('videoEnabled');
  const videoModel = watch('videoModel');
  const durationSecValue = watch('durationSec');
  const videoResolution = watch('videoResolution');

  // toast handed over by the create → edit redirect
  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (state && state.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setLoadError('');
    const loadPreset: Promise<Preset | null> = isEdit && id ? EnterViceService.getPreset(id) : Promise.resolve(null);
    Promise.all([EnterViceService.getProviders(), loadPreset])
      .then(([providerList, loaded]) => {
        if (cancelled) return;
        setProviders(providerList);
        if (loaded) {
          setPreset(loaded);
          reset(toFormValues(loaded));
          keyTouched.current = true;
        } else {
          const firstImage = providerList.find((p) => p.kind === 'image' && p.available) || providerList.find((p) => p.kind === 'image');
          const firstVideo = providerList.find((p) => p.kind === 'video');
          reset({
            ...DEFAULTS,
            imageModel: firstImage ? modelKey(firstImage) : '',
            videoModel: firstVideo ? modelKey(firstVideo) : '',
          });
        }
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(describeError(e, 'Failed to load the preset.').message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, reset]);

  // key follows the name until the admin edits the key by hand (create mode only)
  useEffect(() => {
    if (!isEdit && !keyTouched.current) setValue('key', slugify(nameValue || ''));
  }, [nameValue, isEdit, setValue]);

  const imageProviders = providers.filter((p) => p.kind === 'image');
  const videoProviders = providers.filter((p) => p.kind === 'video');
  const selectedImage = imageProviders.find((p) => modelKey(p) === imageModel);
  const aspectRatios: string[] = (selectedImage && selectedImage.paramsSchema && selectedImage.paramsSchema.aspectRatio) || ['9:16'];
  const resolutions: string[] = (selectedImage && selectedImage.paramsSchema && selectedImage.paramsSchema.resolution) || ['1k'];

  // keep params inside the selected model's schema
  useEffect(() => {
    if (!ready) return;
    const current = getValues();
    if (aspectRatios.indexOf(current.aspectRatio) === -1) setValue('aspectRatio', aspectRatios[0]);
    if (resolutions.indexOf(current.resolution) === -1) setValue('resolution', resolutions[0]);
  }, [imageModel, ready, aspectRatios, resolutions, getValues, setValue]);

  const providerOptions = (list: ProviderDescriptor[]) => list.map((p) => ({
    key: modelKey(p),
    label: p.available ? `${p.label}${p.estCostUsd ? ` · ≈ $${p.estCostUsd.toFixed(2)}/run` : ' · free'}` : `${p.label} (not configured on this environment)`,
  }));

  const readSpec = useCallback((): InlineSpec | null => {
    const v = getValues();
    if (!v.imageModel || !v.prompt || !v.prompt.trim() || !v.negativePrompt || !v.negativePrompt.trim()) return null;
    const image = splitModelKey(v.imageModel);
    return {
      provider: image.provider,
      model: image.model,
      prompt: v.prompt.trim(),
      negativePrompt: v.negativePrompt.trim(),
      params: { aspectRatio: v.aspectRatio, resolution: v.resolution },
    };
  }, [getValues]);

  // WHY: a preset saved without video loads with an empty video model, so enabling the toggle would fail validation
  // on save (and Animate would refuse) until the admin notices the empty dropdown; default it like on create.
  useEffect(() => {
    if (!videoEnabled || getValues('videoModel')) return;
    const firstVideo = providers.find((p) => p.kind === 'video' && p.available) || providers.find((p) => p.kind === 'video');
    if (firstVideo) setValue('videoModel', modelKey(firstVideo), { shouldDirty: true });
  }, [videoEnabled, providers, getValues, setValue]);

  // SCRUM-224: the Test Lab animates a finished image result with whatever the video section holds right now
  const readVideoSpec = useCallback((): InlineVideoSpec | null => {
    const v = getValues();
    if (!v.videoEnabled || !v.videoModel) return null;
    const video = splitModelKey(v.videoModel);
    return {
      provider: video.provider,
      model: video.model,
      motionPrompt: (v.motionPrompt || '').trim(),
      durationSec: Math.min(15, Math.max(1, Number(v.durationSec) || 10)),
      resolution: v.videoResolution || '720p',
    };
  }, [getValues]);

  const refreshPreset = useCallback(() => {
    if (!id) return;
    EnterViceService.getPreset(id)
      .then((fresh) => setPreset(fresh))
      .catch(() => undefined);
  }, [id]);

  const onRunFinished = useCallback((run: TestRun) => {
    if (run.status === 'done') setToast(run.kind === 'video' ? 'Video test finished.' : 'Test finished. You can use the result as thumbnail.');
    refreshPreset();
  }, [refreshPreset]);

  const onThumbnailPromoted = useCallback((thumbnailUrl: string) => {
    setPreset((prev) => (prev ? { ...prev, thumbnailUrl } : prev));
    setToast('Thumbnail updated.');
    refreshPreset();
  }, [refreshPreset]);

  const onSubmit = (values: FormValues) => {
    setSaving(true);
    setFormError(null);
    const payload = toPayload(values);
    const request = isEdit && id
      ? EnterViceService.updatePreset(id, payload).then((updated) => {
        setPreset((prev) => ({ ...(prev as Preset), ...updated, recentTests: prev ? prev.recentTests : undefined }));
        reset(toFormValues(updated));
        setToast('Preset saved.');
      })
      : EnterViceService.createPreset(payload).then((created) => {
        navigate(`/entervice/editPreset/${created._id}`, {
          replace: true,
          state: { toast: 'Draft saved. Run a test in the Test Lab and set a thumbnail to unlock activation.' },
        });
      });
    request
      .catch((e) => setFormError(describeError(e, 'Could not save the preset.')))
      .finally(() => setSaving(false));
  };

  const changeStatus = (status: 'active' | 'inactive') => {
    if (!id) return;
    setStatusBusy(true);
    setStatusError(null);
    EnterViceService.setPresetStatus(id, status)
      .then((updated) => {
        setPreset((prev) => ({ ...(prev as Preset), ...updated, recentTests: prev ? prev.recentTests : undefined }));
        setToast(status === 'active' ? 'Preset activated. It is live in the app.' : 'Preset deactivated.');
      })
      .catch((e) => setStatusError(describeError(e, 'Could not change the status.')))
      .finally(() => setStatusBusy(false));
  };

  const firstFormError = Object.keys(errors).length > 0
    ? ((Object.values(errors)[0] as any)?.message?.toString() as string | undefined)
    : undefined;

  if (loadError) {
    return (
      <div className="cardBg">
        <ValidationError error={loadError} />
        <div className="buttonWrapper">
          <Button buttonType="button" onClick={() => navigate('/entervice')}>Back to presets</Button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return <div className="savingLoader"><CircularProgressLoader /></div>;
  }

  const keyLocked = Boolean(preset && preset.everActive);
  const hasThumbnail = Boolean(preset && preset.thumbnailUrl);
  const hasSuccessfulTest = Boolean(preset && preset.successfulTests > 0);
  const hasSuccessfulVideoTest = Boolean(preset && preset.successfulVideoTests > 0);
  // the saved video block is what vice-service checks; the unsaved toggle only previews the requirement
  const needsVideoTest = Boolean(preset && preset.video) || Boolean(videoEnabled);

  return (
    <>
      {toast && <ToastBar message={toast} handleCloseMethod={() => setToast('')} />}
      <div className="ev-editor">
        <div className="cardBg addDetailsPage">
          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <h2 className="sign_text">{isEdit ? 'Edit Preset' : 'New Preset'}</h2>

            {preset && (
              <div className="ev-section">
                <div className="ev-status-row">
                  <StatusChip status={preset.status} />
                  <span>Prompt <strong>v{preset.version}</strong></span>
                  <span>Rank <strong>{preset.rank ?? '—'}</strong></span>
                  <span>Successful tests <strong>{preset.successfulTests}</strong></span>
                  {needsVideoTest && <span>Video tests <strong>{preset.successfulVideoTests || 0}</strong></span>}
                  {preset.lastTestAt && <span>Last test <strong>{getDateAndTime(preset.lastTestAt)}</strong></span>}
                </div>
                <ul className="ev-checklist">
                  <li className={hasThumbnail ? 'ok' : 'missing'}>Thumbnail set</li>
                  <li className={hasSuccessfulTest ? 'ok' : 'missing'}>At least one successful test run</li>
                  {needsVideoTest && (
                    <li className={hasSuccessfulVideoTest ? 'ok' : 'missing'}>At least one successful video test run (Test Lab → Animate)</li>
                  )}
                </ul>
                <ErrorBox error={statusError} />
                <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.EDIT}>
                  <div className="ev-inline-actions">
                    {preset.status === 'active' ? (
                      <Button buttonType="button" className="ev-btn-ghost" onClick={() => changeStatus('inactive')} disabled={statusBusy}>
                        {statusBusy ? 'Working…' : 'Deactivate'}
                      </Button>
                    ) : (
                      <Button buttonType="button" onClick={() => changeStatus('active')} disabled={statusBusy}>
                        {statusBusy ? 'Working…' : 'Activate'}
                      </Button>
                    )}
                  </div>
                </AccessControl>
              </div>
            )}

            <div className="ev-section">
              <h3>Identity</h3>
              <p className="ev-hint">The key is what the app and analytics use; it locks once the preset has been active.</p>
              <div className="ev-grid2">
                <FieldTextInput id="name" name="name" formName="enterVicePreset" className="textInput" type="text" formProps={formProps} label="*Name" disabled={saving} />
                <FieldTextInput
                  id="key"
                  name="key"
                  formName="enterVicePreset"
                  className="textInput"
                  type="text"
                  formProps={formProps}
                  label="*Key (a-z, 0-9, _)"
                  disabled={saving || keyLocked}
                  handleMainInputChange={() => { keyTouched.current = true; return null; }}
                />
              </div>
              <FieldTextInput id="description" name="description" formName="enterVicePreset" className="textInput" type="textarea" rows={2} maxLength={300} formProps={formProps} label="Description (shown in the app)" disabled={saving} />
            </div>

            <div className="ev-section">
              <h3>Image generation</h3>
              <p className="ev-hint">The source selfie is edited with this prompt. Negative prompt lists what must not appear.</p>
              <FieldSelect id="imageModel" name="imageModel" label="*Model" options={providerOptions(imageProviders)} formProps={formProps} />
              <FieldTextInput id="prompt" name="prompt" formName="enterVicePreset" className="textInput" type="textarea" rows={6} maxLength={4000} formProps={formProps} label="*Prompt" disabled={saving} />
              <FieldTextInput id="negativePrompt" name="negativePrompt" formName="enterVicePreset" className="textInput" type="textarea" rows={3} maxLength={2000} formProps={formProps} label="*Negative prompt" disabled={saving} />
              <div className="ev-grid2">
                <FieldSelect id="aspectRatio" name="aspectRatio" label="Aspect ratio" options={toOptions(aspectRatios)} formProps={formProps} />
                <FieldSelect id="resolution" name="resolution" label="Resolution" options={toOptions(resolutions)} formProps={formProps} />
              </div>
            </div>

            <div className="ev-section">
              <h3>Video (optional)</h3>
              <p className="ev-hint">Animates the finished image. To test these settings, run an image test in the Test Lab and press “Animate” on the result (≈ $0.14 per second of video).</p>
              <label className="ev-toggle" htmlFor="videoEnabled">
                <input id="videoEnabled" type="checkbox" {...register('videoEnabled')} disabled={saving} />
                Enable video for this preset
              </label>
              {videoEnabled && (
                <>
                  <FieldSelect id="videoModel" name="videoModel" label="*Video model" options={providerOptions(videoProviders)} formProps={formProps} />
                  <FieldTextInput id="motionPrompt" name="motionPrompt" formName="enterVicePreset" className="textInput" type="textarea" rows={3} maxLength={2000} formProps={formProps} label="Motion prompt" disabled={saving} />
                  <div className="ev-grid2">
                    <FieldTextInput id="durationSec" name="durationSec" formName="enterVicePreset" className="textInput" type="number" formProps={formProps} label="Duration (1-15 s)" disabled={saving} />
                    <FieldSelect id="videoResolution" name="videoResolution" label="Resolution" options={toOptions(VIDEO_RESOLUTIONS)} formProps={formProps} />
                  </div>
                </>
              )}
            </div>

            <div className="ev-section">
              <h3>Thumbnail</h3>
              <div className="ev-thumbnail">
                <div className="ev-thumbnail-box">
                  {preset && preset.thumbnailUrl
                    ? <img src={preset.thumbnailUrl} alt="preset thumbnail" />
                    : <span>No thumbnail yet</span>}
                </div>
                <div className="ev-thumbnail-text">
                  <strong>Comes from a successful test run.</strong>
                  <br />
                  Run the prompt in the Test Lab, then press “Use as thumbnail” on the result you like.
                  The image is copied and resized to 540×960 for the app.
                  {!isEdit && <><br />Save the preset first to enable this.</>}
                </div>
              </div>
            </div>

            {firstFormError
              ? <ValidationError className="validation_error" error={firstFormError} />
              : <ErrorBox error={formError} />}

            {saving ? (
              <div className="savingLoader"><CircularProgressLoader /></div>
            ) : (
              <div className="buttonWrapper">
                <Button buttonType="submit" disabled={isEdit && !canEdit}>
                  {isEdit ? 'Save changes' : 'Save as draft'}
                </Button>
                <Button buttonType="button" onClick={() => navigate('/entervice')}>Back to list</Button>
              </div>
            )}
          </form>
        </div>

        <TestLab
          presetId={isEdit && id ? id : null}
          providers={providers}
          readSpec={readSpec}
          activeModelKey={imageModel}
          readVideoSpec={readVideoSpec}
          videoForm={{
            enabled: Boolean(videoEnabled),
            modelKey: videoModel || '',
            durationSec: Math.min(15, Math.max(1, Number(durationSecValue) || 10)),
            resolution: videoResolution || '720p',
          }}
          initialRuns={(preset && preset.recentTests) || []}
          canPromote={canEdit}
          onRunFinished={onRunFinished}
          onThumbnailPromoted={onThumbnailPromoted}
        />
      </div>
    </>
  );
};

export default ManageEnterVicePreset;
